import type { AIClient, AIAnalysisRequest, AIAnalysisResult } from '$lib/types/ai';

/**
 * Base abstract class for AI clients
 * Provides common functionality for all AI provider implementations
 */
export abstract class BaseAIClient implements AIClient {
  protected apiKey: string;
  protected model: string;
  protected maxTokens: number;

  constructor(apiKey: string, model: string, maxTokens: number = 2000) {
    this.apiKey = apiKey;
    this.model = model;
    this.maxTokens = maxTokens;
  }

  /**
   * Send a message to the AI provider
   */
  abstract sendMessage(prompt: string): Promise<string>;

  /**
   * Analyze corrections for grammar errors (main method)
   */
  abstract analyzeCorrections(request: AIAnalysisRequest): Promise<AIAnalysisResult[]>;

  /**
   * Check if the client is available
   */
  abstract isAvailable(): boolean | Promise<boolean>;

  /**
   * Get the model name
   */
  abstract getModelName(): string;

  /**
   * Check if running in Electron environment
   */
  protected isElectron(): boolean {
    return typeof window !== 'undefined' && 
           !!window.api && 
           typeof window.api.aiRequest === 'function';
  }

  /**
   * Make API request through Electron IPC or direct fetch
   */
  protected async makeRequest(
    provider: string,
    url: string,
    body: any
  ): Promise<any> {
    if (this.isElectron()) {
      // Use Electron IPC for API requests
      const result = await window.api!.aiRequest({
        provider,
        url,
        headers: this.getHeaders(),
        body
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data;
    } else {
      // Direct fetch for development environment
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `${provider} API error: ${response.status}`;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error?.message || errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      return await response.json();
    }
  }

  /**
   * Get headers for API requests
   * Override in subclasses for provider-specific headers
   */
  protected getHeaders(): Record<string, string> {
    return {
      'Authorization': `Bearer ${this.apiKey}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Parse grammar errors from AI response
   * Common parsing logic that can be overridden
   */
  protected parseGrammarErrors(content: string): any[] {
    const errors: any[] = [];
    
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      if (parsed.errors && Array.isArray(parsed.errors)) {
        return parsed.errors;
      }
    } catch {
      // If not JSON, parse as structured text
      const lines = content.split('\n');
      let currentError: any = null;
      
      for (const line of lines) {
        // Parse error patterns from text response
        // This is a simplified version - override in subclasses for better parsing
        if (line.match(/^\d+\./)) {
          if (currentError) {
            errors.push(currentError);
          }
          currentError = {
            message: line.replace(/^\d+\.\s*/, ''),
            corrections: [] as any[]
          };
        } else if (currentError && line.trim()) {
          (currentError.corrections as any[]).push({
            text: line.trim()
          });
        }
      }
      
      if (currentError) {
        errors.push(currentError);
      }
    }
    
    return errors;
  }
}